import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import React, { useContext, useEffect, useState } from 'react';
import { Button, Container, Row } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import ModalBody from 'react-bootstrap/ModalBody';
import IconButton from '../../../components/IconButton/IconButton';
import ModalHeader from '../../../components/ModalHeader';
import { RadioGroup } from '../../../components/Radio';
import SelectableCard from '../../../components/SelectableCard';
import Link from '../../../components/Link';
import DefaultCard from '../../../components/DefaultCard';
import { EnrollmentStore } from '../../../providers/enrollment';
import { getAllNonTobaccoUserCredits } from '../../../services/enrollment';

export type customNonTobaccoUserCreditModalProps = {
  showNonTobaccoUserCreditModal: boolean;
  handleCloseNonTobaccoUserCreditModal: () => void;
};

const NonTobaccoUserCreditModal = ({
  showNonTobaccoUserCreditModal,
  handleCloseNonTobaccoUserCreditModal,
}: customNonTobaccoUserCreditModalProps) => {
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [nonTobaccoUserCreditData, setNonTobaccoUserCreditData] =
    React.useState<any>({});

  const { activeHealthBenefits, updateCost, updatePlanName } =
    useContext(EnrollmentStore);

  const [radioButtonValue, setRadioButtonValue] = React.useState<
    string | number
  >(9);

  const [tellMeMore, setTellMeMore] = useState(false);

  useEffect(() => {
    const data = getAllNonTobaccoUserCredits();

    if (data) {
      setNonTobaccoUserCreditData(data);
    }
  }, [nonTobaccoUserCreditData]);

  useEffect(() => {
    const currentBenefit = activeHealthBenefits.find(
      (benefit: any) => benefit.type === 'non-tobacco-user-credit'
    );
    const index = nonTobaccoUserCreditData.plans?.findIndex(
      (plan: any) => plan.id === currentBenefit?.plan?.id
    );
    setRadioButtonValue(index);
  }, [activeHealthBenefits, nonTobaccoUserCreditData]);

  const handleRadioButtonValue = (e: string | number) => {
    setRadioButtonValue(e);
  };

  const getPrePaidPrice = (costType: string) => {
    switch (costType) {
      case 'credit':
        return 'credit';
      default:
        return '';
    }
  };

  const getPrePaidPriceColor = (costType: string) => {
    switch (costType) {
      case 'credit':
        return true;
      default:
        return false;
    }
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const handleTellMeMore = () => {
    setTellMeMore((prev) => !prev);
  };

  return (
    <>
      {nonTobaccoUserCreditData ? (
        <Modal
          show={showNonTobaccoUserCreditModal}
          onHide={handleCloseNonTobaccoUserCreditModal}
          dialogClassName="modal-xl"
          className="custom-health-savings-modal"
        >
          <ModalHeader
            leftIcon={light('heart-pulse')}
            leftIconVariant="icon-emphasized"
            rightButton={
              <div onClick={toggleDetails} className={'icon-button-wrapper'}>
                <IconButton
                  variant="icon-default"
                  icon={light('circle-exclamation')}
                  extLabel="Benefit Info"
                />
              </div>
            }
          >
            <h4 className="text-sm-20-lg-32">
              {nonTobaccoUserCreditData['generic']
                ? nonTobaccoUserCreditData['generic']['title']
                : ''}
            </h4>
          </ModalHeader>
          <ModalBody>
            <Row className={`expandable-wrapper ${showDetails ? 'open' : ''}`}>
              <div className="d-flex justify-content-center">
                <DefaultCard
                  closeButton={
                    <Link 
                      className='pt-0' 
                      isAlignAtEnd 
                      isWhiteIcon 
                      rightIcon={light('close')}
                      onClick={toggleDetails}
                    />                      
                  }
                  variant="blue-motif"
                  leftHeading={
                    nonTobaccoUserCreditData['generic']
                      ? nonTobaccoUserCreditData['generic']['info']['title']
                      : ''
                  }
                  leftSubheading={
                    <Container className={'px-0 d-flex flex-column'}>
                      {nonTobaccoUserCreditData['generic'] ? (
                        <>
                          <span>
                            {
                              nonTobaccoUserCreditData['generic']['info'][
                                'description'
                              ]
                            }
                          </span>
                          {tellMeMore && (
                            <Container className={'px-0 d-flex flex-column'}>
                              {nonTobaccoUserCreditData['generic']['info'][
                                'additionalDescription'
                              ].map((d: string) => (
                                <span className={'mt-1'}>{d}</span>
                              ))}
                            </Container>
                          )}
                        </>
                      ) : (
                        ''
                      )}
                    </Container>
                  }
                  button={
                    <Button
                      onClick={handleTellMeMore}
                      variant="primary-shadow"
                      size="sm"
                    >
                      {tellMeMore ? 'Tell me less' : 'Tell me more'}
                    </Button>
                  }
                  className={'mb-12'}
                />
              </div>
            </Row>
            <Container className="p-0">
              <Row className="mb-7 mb-lg-12">
                <h5 className="text-sm-14-lg-24">
                  What option would you like?
                </h5>
              </Row>
              <Row>
                <RadioGroup
                  name={'pricing-test1'}
                  value={radioButtonValue}
                  onChange={handleRadioButtonValue}
                >
                  <div>
                    {nonTobaccoUserCreditData['plans']
                      ? nonTobaccoUserCreditData['plans'].map(
                          (
                            plan: {
                              title: string;
                              cost: number;
                              coverage: number;
                              costType: string;
                            },
                            i: any
                          ) => (
                            <SelectableCard
                              title={plan.title}
                              prePaydPeriod={Math.abs(plan.cost)}
                              costType={getPrePaidPrice(plan.costType)}
                              isCredit={getPrePaidPriceColor(plan.costType)}
                              radioButtonValue={i}
                              selected={radioButtonValue === i}
                              key={i}
                            />
                          )
                        )
                      : ''}
                  </div>
                </RadioGroup>
              </Row>
            </Container>
          </ModalBody>
          <Modal.Footer className="pt-7">
            <Button
              onMouseDown={handleCloseNonTobaccoUserCreditModal}
              className="text-nowrap mt-7 mt-lg-0"
              variant="primary-outline"
            >
              Cancel
            </Button>
            <Button
              onMouseDown={() => {
                updateCost(
                  'non-tobacco-user-credit',
                  nonTobaccoUserCreditData.plans[radioButtonValue]
                );
                updatePlanName(
                  'non-tobacco-user-credit',
                  nonTobaccoUserCreditData.plans[radioButtonValue]
                );
                handleCloseNonTobaccoUserCreditModal();
              }}
              className="text-nowrap"
              variant="primary"
            >
              Continue
            </Button>
          </Modal.Footer>
        </Modal>
      ) : (
        'NO DATA'
      )}
    </>
  );
};

export default NonTobaccoUserCreditModal;
